import { useState } from 'react';
import * as S from '../../domain/sceneOps';
import type { Scene } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import { Button, Panel, inputClass } from '../ui';
import { layerLookBadge } from '../../scene/look';
import { LayerLookControls } from './LookControls';
import type { EditFn } from './useSceneEdit';

const icon = 'inline-flex size-8 items-center justify-center rounded text-xs hover:bg-zinc-800 aria-pressed:text-zinc-500';

/**
 * Layers (top of the list = drawn last) with their objects. Doubles as the accessible
 * object list: every object can be selected, shown/hidden and locked from here, including
 * locked objects that the canvas ignores.
 */
export function LayerPanel({ scene, edit }: { scene: Scene; edit: EditFn }) {
  const { selection } = useServices();
  const selected = useObservable(selection);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  const sid = scene.id;
  const topDown = [...scene.layers].reverse();
  const activeLayer = scene.layers.find((l) => l.id === selected.layerId)?.id ?? null;

  return (
    <Panel
      title="Layers"
      headingLevel={3}
      actions={
        <Button size="sm" onClick={() => edit('Add layer', (p) => S.addLayer(p, sid))} data-testid="add-layer">
          + Layer
        </Button>
      }
    >
      <ol className="flex flex-col gap-2" aria-label="Layers, top to bottom" data-testid="layer-list">
        {topDown.map((layer) => {
          const objects = scene.objects.filter((o) => o.layerId === layer.id).reverse();
          const index = scene.layers.indexOf(layer);
          return (
            <li key={layer.id} className={`rounded-md border ${activeLayer === layer.id ? 'border-accent-400' : 'border-zinc-800'} bg-zinc-900`} data-testid="layer">
              <div className="flex items-center gap-1 px-1.5 py-1">
                <button
                  type="button"
                  className={icon}
                  aria-pressed={!layer.visible}
                  aria-label={`${layer.visible ? 'Hide' : 'Show'} layer ${layer.name}`}
                  onClick={() => edit(layer.visible ? 'Hide layer' : 'Show layer', (p) => S.updateLayer(p, sid, layer.id, { visible: !layer.visible }))}
                >
                  {layer.visible ? '👁' : '—'}
                </button>
                <button
                  type="button"
                  className={icon}
                  aria-pressed={layer.locked}
                  aria-label={`${layer.locked ? 'Unlock' : 'Lock'} layer ${layer.name}`}
                  onClick={() => edit(layer.locked ? 'Unlock layer' : 'Lock layer', (p) => S.updateLayer(p, sid, layer.id, { locked: !layer.locked }))}
                >
                  {layer.locked ? '🔒' : '🔓'}
                </button>
                {renaming?.id === layer.id ? (
                  <form
                    className="flex flex-1 gap-1"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (edit('Rename layer', (p) => S.updateLayer(p, sid, layer.id, { name: renaming.name }))) setRenaming(null);
                    }}
                  >
                    <label htmlFor={`layer-name-${layer.id}`} className="sr-only">
                      Layer name
                    </label>
                    <input id={`layer-name-${layer.id}`} ref={(el) => el?.focus()} value={renaming.name} maxLength={60} onChange={(e) => setRenaming({ id: layer.id, name: e.target.value })} onKeyDown={(e) => e.key === 'Escape' && setRenaming(null)} className={`${inputClass} py-1`} />
                  </form>
                ) : (
                  <button type="button" className="flex-1 truncate text-left text-sm font-medium" aria-pressed={activeLayer === layer.id} onClick={() => selection.selectLayer(activeLayer === layer.id ? null : layer.id)} onDoubleClick={() => setRenaming({ id: layer.id, name: layer.name })} data-testid="layer-select">
                    {layer.name} <span className="text-xs text-zinc-400">({objects.length})</span>
                    {layerLookBadge(layer) && <span className="ml-1 rounded bg-zinc-800 px-1 text-[10px] text-zinc-300" data-testid="layer-look-badge">{layerLookBadge(layer)}</span>}
                  </button>
                )}
                <button type="button" className={icon} aria-label={`Rename layer ${layer.name}`} onClick={() => setRenaming({ id: layer.id, name: layer.name })}>
                  ✎
                </button>
                <button type="button" className={icon} aria-label={`Move layer ${layer.name} up`} disabled={index === scene.layers.length - 1} onClick={() => edit('Reorder layers', (p) => S.moveLayer(p, sid, layer.id, 1))}>
                  ▲
                </button>
                <button type="button" className={icon} aria-label={`Move layer ${layer.name} down`} disabled={index === 0} onClick={() => edit('Reorder layers', (p) => S.moveLayer(p, sid, layer.id, -1))}>
                  ▼
                </button>
                <button type="button" className={`${icon} text-red-300`} aria-label={`Delete layer ${layer.name}`} disabled={scene.layers.length <= 1} onClick={() => edit('Delete layer', (p) => S.deleteLayer(p, sid, layer.id))}>
                  ✕
                </button>
              </div>
              {activeLayer === layer.id && <LayerLookControls layer={layer} edit={edit} sceneId={sid} />}
              {objects.length > 0 && (
                <ul className="max-h-48 overflow-y-auto border-t border-zinc-800 py-1" aria-label={`Objects on ${layer.name}`}>
                  {objects.map((o) => {
                    const isSel = selected.objectIds.includes(o.id);
                    return (
                      <li key={o.id} className={`flex items-center gap-1 px-1.5 ${isSel ? 'bg-accent-600/20' : ''}`}>
                        <button
                          type="button"
                          className={icon}
                          aria-pressed={!o.visible}
                          aria-label={`${o.visible ? 'Hide' : 'Show'} ${o.name}`}
                          onClick={() => edit(o.visible ? 'Hide' : 'Show', (p) => S.setVisible(p, sid, [o.id], !o.visible))}
                        >
                          {o.visible ? '👁' : '—'}
                        </button>
                        <button type="button" className={icon} aria-pressed={o.locked} aria-label={`${o.locked ? 'Unlock' : 'Lock'} ${o.name}`} onClick={() => edit(o.locked ? 'Unlock' : 'Lock', (p) => S.setLocked(p, sid, [o.id], !o.locked))}>
                          {o.locked ? '🔒' : '🔓'}
                        </button>
                        <button
                          type="button"
                          aria-pressed={isSel}
                          className="flex-1 truncate py-1 text-left text-xs"
                          onClick={(e) => selection.selectObjects(e.shiftKey || e.ctrlKey || e.metaKey ? (isSel ? selected.objectIds.filter((id) => id !== o.id) : [...selected.objectIds, o.id]) : [o.id])}
                          data-testid="object-row"
                        >
                          <span className="text-zinc-400">{o.type} · </span>
                          {o.name}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ol>
      {!activeLayer && <p className="mt-2 text-xs text-zinc-400">Select a layer’s name to set its opacity and blend mode.</p>}
    </Panel>
  );
}
